import { ResultOf } from "gql.tada";
import { HeroById } from "@queries/entries/HeroById";
import { DynamicScaling } from "@components/shared/DynamicScaling";
import { MarkdownLite } from "@components/shared/MarkdownLite";
import { CtaButton } from "@components/entries/shared/CtaButton";
import { Analytics } from "@utils/Analytics";
import { useGlobalContext } from "@utils/GlobalContext";
import { useTranslation } from "@services/i18n";
import { OBELIX_COMMON_KEYS } from "@utils/obelixKeys";

export const VideoWithCopyHero = ({
  entry: { hero: entry },
}: {
  entry: ResultOf<typeof HeroById>;
}) => {
  const { locale } = useGlobalContext();
  const { t } = useTranslation();

  if (!entry) return null;

  const handleCtaClick = () => {
    Analytics.trackEvent(`still_watching_${locale}_report_cta_click`);
  };

  const renderOverlayImage = () => {
    if (!entry?.overlayImage || 
        typeof entry.overlayImage !== 'object' || 
        !('url' in entry.overlayImage) || 
        !entry.overlayImage.url ||
        typeof entry.overlayImage.url !== 'string') {
      return null;
    }
    
    return (
      <div className="absolute inset-0 z-[5] flex items-center justify-center p-4 sm:p-6 md:p-8 lg:p-12">
        <img
          src={entry.overlayImage.url}
          alt={t(
            OBELIX_COMMON_KEYS.heroOverlay.obelixKey,
            OBELIX_COMMON_KEYS.heroOverlay.label
          )}
          className="w-full h-full object-contain max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)] dsk:max-h-[calc(75vh-2rem)] dsk:max-w-[calc(75vw-2rem)]"
        />
      </div>
    );
  };

  return (
    <div className="w-full bg-brand-black min-h-screen flex flex-col justify-center items-center pb-10 px-12 relative">
      {!!entry?.previewVideo?.url && (
        <video
          autoPlay
          loop
          playsInline
          muted
          src={entry.previewVideo.url}
          className="absolute inset-0 size-full object-cover"
        />
      )}
      
      {renderOverlayImage()}
      
      <div className="z-10 flex flex-col items-center justify-center gap-9 max-w-screen">
        <DynamicScaling>
          <h1 className="text-heading-1 dsk:text-[200px] uppercase text-center">
            {entry.title}
          </h1>
        </DynamicScaling>
        
        {entry.description && (
          <p className="text-body-regular text-center max-w-[450px] text-brand-cream">
            <MarkdownLite>{entry.description}</MarkdownLite>
          </p>
        )}
      </div>
      
      {entry.ctaText && entry.ctaLink && (
        <div className="z-10 absolute bottom-10 left-0 right-0 px-4 flex justify-center">
          <CtaButton 
            ctaText={entry.ctaText} 
            ctaLink={entry.ctaLink}
            variant="light-on-blur"
            showArrowOnMobile={true}
            onClick={handleCtaClick}
          />
        </div>
      )}
    </div>
  );
};
